import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/insidePages';
import { OFFER_ANCHOR, PRODUCT } from '../data/constants';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const scrollTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setActive(clamped);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const next = Math.round(track.scrollLeft / track.clientWidth);
      setActive(Math.max(0, Math.min(next, insidePages.length - 1)));
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 z-10 -translate-y-1/2 rounded-full border-2 border-white bg-aviation p-2 text-white shadow-lg transition-[background-color,opacity] duration-150 ease-out hover:bg-aviation-dark disabled:pointer-events-none disabled:opacity-0 sm:p-3';
  return <section className="border-y-4 border-gold bg-white py-5 sm:py-7" data-id="element-89" data-mp="f32ege939lhj">
      <div className="mx-auto max-w-5xl px-4 sm:px-6" data-id="element-90" data-mp="3nh17p84oesrc">
        <h2 className="text-center text-3xl leading-none text-navy sm:text-5xl" data-id="element-91" data-mp="3nfhrtimxmjq3">
          LOOK <span className="text-aviation" data-id="element-92" data-mp="3ik2d6oh3ojsm">INSIDE</span>
        </h2>
        <p className="mt-1 text-center font-display text-sm font-semibold uppercase leading-tight text-navy sm:text-lg" data-id="element-93" data-mp="129evf3xk07k">
          See {PRODUCT} In Action
        </p>
        <p className="mx-auto mt-1 max-w-3xl text-center text-sm leading-snug text-graphite sm:text-base" data-id="element-94" data-mp="34r30eu8pd3or">
          Explore annotated aircraft system diagrams, detailed cockpit instrument illustrations,
          aerodynamic visualizations, airspace charts, navigation references, weather
          interpretation examples, and structured flight procedure learning resources.
        </p>

        <div className="relative mt-3" data-id="element-95" data-mp="18lllyn0z9mut">
          <div ref={trackRef} className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Interior pages carousel" data-id="element-96" data-mp="1deep56gssn4s">
            {insidePages.map((page, i) => <div key={page.src} className="w-full shrink-0 snap-center px-0.5" role="group" aria-roledescription="slide" aria-label={`Page ${i + 1} of ${insidePages.length}`} data-id="element-97" data-mp="ppvievdeciet" data-mpi={i}>
                <a href={OFFER_ANCHOR} className="mx-auto block max-w-xl overflow-hidden rounded-xl border-2 border-navy bg-white shadow-xl" data-id="element-98" data-mp="256m5e5se5xin">
                  <img src={page.src} alt={page.alt} loading={i === 0 ? 'eager' : 'lazy'} className="block w-full object-contain" data-id="element-99" data-mp="2k9dsr38p1d1i" />
                </a>
              </div>)}
          </div>

          <button type="button" onClick={() => scrollTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={`${arrowClass} left-1 sm:left-4`} data-id="element-100" data-mp="26hzbb0ufpjwk">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={3} data-id="element-101" data-mp="1whoxpf4hmagv" />
          </button>
          <button type="button" onClick={() => scrollTo(active + 1)} disabled={active === insidePages.length - 1} aria-label="Next page" className={`${arrowClass} right-1 sm:right-4`} data-id="element-102" data-mp="1rkyh8qortcux">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={3} data-id="element-103" data-mp="3hwmjbcnjxcad" />
          </button>
        </div>

        <div className="mt-2.5 flex items-center justify-center gap-2" data-id="element-104" data-mp="3p02jx2tikvdv">
          {insidePages.map((page, i) => <button key={page.src} type="button" onClick={() => scrollTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-150 ease-out ${i === active ? 'w-7 bg-aviation' : 'w-2 bg-navy/25 hover:bg-navy/50'}`} data-id="element-105" data-mp="3tzfeyaujh8rj" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}